<template>
    <!-- Desktop view - Datatable -->
    <time-datatable 
        v-if="!isMobile && !props.embedded"
        :request-id="props.requestId"
        :client-id="props.clientId"
        :tech-id="props.techId"
        :hide-filters="props.hideFilters"
        :embedded="props.embedded"
        :pm-only="props.pmOnly"
        :disable-actions="props.disableActions"
    />
    
    <!-- Mobile view - Card list -->
    <time-mobile 
        v-else-if="isMobile && !props.embedded"
        :rows="rows"
        :total="total"
        :filter="legacyFilter"
        @refresh="refresh"
        @new-record="newRecord"
        @edit-record="editRecord"
        @set-filter="setFilter"
    />
    
    <!-- Embedded view (always use datatable for now) -->
    <time-datatable 
        v-else
        :request-id="props.requestId"
        :client-id="props.clientId"
        :tech-id="props.techId"
        :hide-filters="props.hideFilters"
        :embedded="props.embedded"
        :pm-only="props.pmOnly"
        :disable-actions="props.disableActions"
    />
    
    <router-view name="modal"></router-view>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import { useRoute, useRouter, RouterView } from "vue-router";
import Api from "@/services/ApiService";
import { ApiResponse } from "@/services/ApiService";
import { TimeModel, TimeModelFilter } from "@/models/TaskModel";
import Cache from "@/services/Cache";
import TimeDatatable from "@/components/Time/TimeDatatable.vue";
import TimeMobile from "@/components/Time/TimeMobile.vue";
import Helper from "@/services/Helper";

const route = useRoute();
const router = useRouter();

// Props
const props = defineProps({
    requestId: {
        type: [Number, String],
        default: null
    },
    clientId: {
        type: [Number, String],
        default: null
    },
    techId: {
        type: [Number, String],
        default: null
    },
    hideFilters: {
        type: Boolean,
        default: false
    },
    embedded: {
        type: Boolean,
        default: false
    },
    pmOnly: {
        type: Boolean,
        default: false
    },
    disableActions: {
        type: Boolean,
        default: false
    }
});

// ===== MOBILE DETECTION =====

const isMobile = ref(false);
let mediaQuery: MediaQueryList | null = null;

const checkMobile = () => {
    // Bootstrap's md breakpoint is 768px
    if (typeof window !== 'undefined') {
        isMobile.value = window.innerWidth < 768;
    }
};

const handleMediaChange = (e: MediaQueryListEvent) => {
    isMobile.value = e.matches;
};

onMounted(() => {
    if (typeof window !== 'undefined') {
        mediaQuery = window.matchMedia('(max-width: 767px)');
        isMobile.value = mediaQuery.matches;
        mediaQuery.addEventListener('change', handleMediaChange);
        
        // For mobile view, load data
        if (isMobile.value && !props.embedded) {
            initializeFilter();
            loadData();
        }
    }
});

onUnmounted(() => {
    if (mediaQuery) {
        mediaQuery.removeEventListener('change', handleMediaChange);
    }
});

// ===== DATA STATE (for mobile view) =====

const total = ref(0);
const rows = ref<TimeModel[]>([]);

// Filter state interface
interface TimeListFilter {
    timeType: string;
    client: number[];
    technician: number[];
    user: string;
    startDate: string;
    endDate: string;
    page: number;
    size: number;
    sort: Array<Record<string, 'ASC' | 'DESC'>>;
}

// Current filter state (reactive)
const currentFilter = ref<TimeListFilter>({
    timeType: 'All',
    client: [],
    technician: [],
    user: '',
    startDate: '',
    endDate: '',
    page: 1,
    size: 25,
    sort: []
});

// Legacy filter format for mobile component
const legacyFilter = computed(() => ({
    client: currentFilter.value.client.map(c => c.toString()).join(','),
    technician: currentFilter.value.technician.map(t => t.toString()).join(','),
    user: currentFilter.value.user,
    startDate: currentFilter.value.startDate,
    endDate: currentFilter.value.endDate,
    page: currentFilter.value.page.toString(),
    type: currentFilter.value.timeType.toLowerCase()
}));

// ===== URL & FILTER UTILITIES =====

// Parse URL params to filter object
const parseUrlToFilter = (): TimeListFilter => {
    const routeQuery = route.query;
    
    return {
        timeType: (routeQuery.timeType as string) || 'All',
        client: Array.isArray(routeQuery.client) 
            ? routeQuery.client.filter(c => c !== null).map(c => parseInt(c as string))
            : (routeQuery.client ? [parseInt(routeQuery.client as string)] : []),
        technician: Array.isArray(routeQuery.technician) 
            ? routeQuery.technician.filter(t => t !== null).map(t => parseInt(t as string))
            : (routeQuery.technician ? [parseInt(routeQuery.technician as string)] : []),
        user: (routeQuery.user as string) || '',
        startDate: (routeQuery.startDate as string) || '',
        endDate: (routeQuery.endDate as string) || '',
        page: parseInt((routeQuery.page as string) || '1'),
        size: parseInt((routeQuery.size as string) || '25'),
        sort: []
    };
};

// Build URL params from filter object
const buildUrlFromFilter = (filter: TimeListFilter): string => {
    const params = new URLSearchParams();
    
    filter.client.forEach(c => c && params.append('client', c.toString()));
    filter.technician.forEach(t => t && params.append('technician', t.toString()));
    
    if (filter.timeType !== 'All') params.set('timeType', filter.timeType);
    if (filter.user) params.set('user', filter.user);
    if (filter.startDate) params.set('startDate', filter.startDate);
    if (filter.endDate) params.set('endDate', filter.endDate);
    
    params.set('page', filter.page.toString());
    if (filter.size !== 25) params.set('size', filter.size.toString());
    
    return params.toString();
};

// Save filter to Cache
const saveFilterToStorage = (filter: TimeListFilter) => {
    try {
        Cache.set('time-list-filter', filter);
    } catch (error) {
        console.error('Failed to save filter to cache:', error);
    }
};

// Load filter from Cache
const loadFilterFromStorage = (): TimeListFilter => {
    try {
        const stored = Cache.get('time-list-filter');
        if (stored) return stored;
    } catch (error) {
        console.error('Failed to load filter from cache:', error);
    }
    
    return {
        timeType: 'All',
        client: [],
        technician: [],
        user: '',
        startDate: '',
        endDate: '',
        page: 1,
        size: 25,
        sort: []
    };
};

// ===== API INTEGRATION =====

// Convert filter to API parameters
const buildApiParams = (filter: TimeListFilter) => {
    const page = Math.max(1, filter.page || 1);
    
    const params: any = {
        Page: page - 1,
        Size: filter.size || 25,
        IncludeRequest: true,
        IncludeContact: true
    };
    
    if (props.requestId) {
        params.RequestId = props.requestId;
        return params;
    }
    
    if (filter.client.length > 0) params.CustomerNumber = filter.client;
    if (filter.technician.length > 0) params.TechId = filter.technician;
    if (filter.user) params.TechId = filter.user;
    if (filter.startDate) params.StartDate = new Date(filter.startDate + " 00:00:00");
    if (filter.endDate) params.EndDate = new Date(filter.endDate + " 23:59:59");
    if (filter.timeType !== 'All') params.TimeType = filter.timeType;
    
    return params;
};

// Load data from API
const loadData = async () => {
    try {
        const params = buildApiParams(currentFilter.value);
        const response: ApiResponse<TimeModel[]> = await Api.loadTimes(params, false);
        
        rows.value = response.Data || [];
        total.value = response.Meta?.Total || 0;
    } catch (error) {
        console.error('Failed to load data:', error);
        rows.value = [];
        total.value = 0;
    }
};

// Initialize filter from URL or localStorage
const initializeFilter = () => {
    if (props.requestId) return;
    
    const hasUrlParams = Object.keys(route.query).length > 0;
    
    if (hasUrlParams) {
        currentFilter.value = parseUrlToFilter();
    } else {
        const cachedFilter = loadFilterFromStorage();
        currentFilter.value = cachedFilter;
        
        const urlParams = buildUrlFromFilter(currentFilter.value);
        const newUrl = `/time/list${urlParams ? '?' + urlParams : ''}`;
        router.replace(newUrl);
    }
};

// ===== EVENT HANDLERS FOR MOBILE VIEW =====

const refresh = () => {
    loadData();
};

const newRecord = () => {
    router.push({ path: "/time/new" });
};

const editRecord = (id: number) => {
    router.push({ path: "/time/edit/" + id });
};

const setFilter = (filterString: string) => {
    router.push({ path: "/time/list/" + filterString });
};

// ===== ROUTE WATCHER (for mobile view) =====

watch(() => route.query, (newQuery, oldQuery) => {
    if (props.requestId || !isMobile.value) return;
    
    const newFilter = parseUrlToFilter();
    const currentParams = buildUrlFromFilter(currentFilter.value);
    const newParams = buildUrlFromFilter(newFilter);
    
    if (currentParams !== newParams) {
        currentFilter.value = newFilter;
        saveFilterToStorage(newFilter);
        loadData();
    }
}, { deep: true });

</script>
